import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import UserAvatar from './UserAvatar';
import {
  FiX,
  FiHome,
  FiUsers,
  FiFileText,
  FiTrendingDown,
  FiBarChart2,
  FiUser,
  FiSettings,
  FiZap,
  FiAward,
  FiMail,
  FiLogOut,
  FiChevronRight,
  FiEye,
  FiDollarSign,
} from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import { getUserPlanInfo, getPlanBadgeClasses } from '../utils/planUtils';
import { isViewerHome } from '../utils/access';
import { canAccessVistaContador } from '../utils/contadorAccess';
import { canAccessTeam } from '../utils/teamAccess';
import { needsSubscriptionPayment } from '../utils/subscriptionGate';

const guestLinks = [
  { to: '/', label: 'Inicio', icon: FiHome },
  { to: '/funcionalidades', label: 'Funcionalidades', icon: FiZap },
  { to: '/planes', label: 'Planes', icon: FiAward },
  { to: '/quienes-somos', label: 'Quiénes somos', icon: FiUsers },
];

const coreAppLinks = [
  { to: '/clientes', label: 'Clientes', icon: FiUsers },
  { to: '/facturas', label: 'Registro de ventas', icon: FiFileText },
  { to: '/egresos', label: 'Egresos', icon: FiTrendingDown },
  { to: '/reportes', label: 'Reportes', icon: FiBarChart2 },
  { to: '/cobranzas', label: 'Cobrar', icon: FiDollarSign },
];

function appLinksFor(user) {
  if (needsSubscriptionPayment(user)) {
    return [
      { to: '/planes?pago=pendiente', label: 'Regularizar pago', icon: FiAward },
      { to: '/ayuda', label: 'Ayuda', icon: FiMail },
    ];
  }

  const contadorLink = canAccessVistaContador(user)
    ? [{ to: '/contador', label: 'Vista contador', icon: FiEye }]
    : [];
  const teamLink = canAccessTeam(user)
    ? [{ to: '/equipo/usuarios', label: 'Equipo', icon: FiUsers }]
    : [];

  if (isViewerHome(user)) {
    return [...contadorLink, ...coreAppLinks, ...teamLink];
  }
  return [
    { to: '/inicio', label: 'Inicio', icon: FiHome },
    ...coreAppLinks,
    ...contadorLink,
    ...teamLink,
  ];
}

const isActivePath = (pathname, to) => {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
};

const MenuItem = ({ to, icon: Icon, label, active, onClick, modoOscuro }) => {
  const iconBox = active
    ? modoOscuro
      ? 'bg-[#004AAD]/30 text-[#5CA3FF]'
      : 'bg-[#004AAD]/10 text-[#004AAD]'
    : modoOscuro
      ? 'bg-gray-800 text-gray-300'
      : 'bg-gray-100 text-gray-600';
  const row = active
    ? modoOscuro
      ? 'bg-[#004AAD]/20 text-white'
      : 'bg-[#004AAD]/10 text-[#004AAD]'
    : modoOscuro
      ? 'text-gray-100 hover:bg-white/5'
      : 'text-gray-800 hover:bg-gray-50';

  const content = (
    <>
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconBox}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 text-sm font-medium text-left">{label}</span>
      <FiChevronRight className={`w-4 h-4 shrink-0 ${active ? '' : 'opacity-40'}`} />
    </>
  );

  const className = `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${row}`;

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={className} aria-current={active ? 'page' : undefined}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
};

const MenuMobile = ({ open, onClose }) => {
  const { user, logout } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [rendered, setRendered] = useState(false);
  const [visible, setVisible] = useState(false);
  const planInfo = useMemo(() => (user ? getUserPlanInfo(user) : null), [user]);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const id = window.setTimeout(() => {
        requestAnimationFrame(() => setVisible(true));
      }, 20);
      return () => window.clearTimeout(id);
    }
    if (rendered) {
      setVisible(false);
      const timeout = window.setTimeout(() => setRendered(false), 320);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [open, rendered]);

  useEffect(() => {
    if (!rendered || !visible) return undefined;
    const main = document.getElementById('main-scroll');
    const prevBody = document.body.style.overflow;
    const prevMain = main ? main.style.overflow : '';
    document.body.style.overflow = 'hidden';
    if (main) main.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      if (main) main.style.overflow = prevMain;
    };
  }, [rendered, visible]);

  useEffect(() => {
    if (!rendered) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [rendered, onClose]);

  if (!rendered) return null;

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/login');
  };

  const panel = modoOscuro ? 'bg-black text-white border-gray-800' : 'bg-white text-gray-900 border-gray-200';
  const divide = modoOscuro ? 'border-gray-800' : 'border-gray-200';
  const isSuperAdmin = user?.role === 'admin' && user?.isSuperAdmin === true;
  const subscriptionBlocked = user && needsSubscriptionPayment(user);

  return createPortal(
    <div
      className={`fixed inset-0 z-[99999] flex justify-end transition-colors duration-300 ${
        visible ? 'bg-black/50' : 'bg-black/0 pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Menú"
    >
      <button
        type="button"
        className="flex-1 min-w-0 h-full cursor-default"
        aria-label="Cerrar menú"
        onClick={onClose}
        tabIndex={-1}
      />

      <aside
        className={`
          flex flex-col w-[min(86vw,22rem)] h-full border-l shadow-2xl
          pt-[max(0.75rem,env(safe-area-inset-top))]
          pb-[max(0.75rem,env(safe-area-inset-bottom))]
          transition-transform duration-300 ease-out will-change-transform
          ${panel}
          ${visible ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className={`flex items-center justify-between gap-3 px-5 pb-4 border-b ${divide}`}>
          {user ? (
            <Link
              to="/cuenta"
              onClick={onClose}
              className="flex items-center gap-3 min-w-0 flex-1 rounded-xl hover:opacity-90"
            >
              <UserAvatar user={user} size="md" rounded="2xl" />
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-sm truncate">{user.name}</span>
                {planInfo && (
                  <span className={`mt-1 inline-flex self-start text-[11px] font-semibold px-2 py-0.5 rounded-full border ${getPlanBadgeClasses(planInfo.slug, modoOscuro)}`}>
                    Plan {planInfo.name}
                  </span>
                )}
              </div>
            </Link>
          ) : (
            <img
              src={modoOscuro ? '/logowhite.png' : '/logoblack.png'}
              alt="Contsuite"
              className="h-7 w-auto"
            />
          )}
          <button
            type="button"
            className={`p-2 rounded-xl shrink-0 transition-colors ${
              modoOscuro
                ? 'text-gray-400 hover:text-white hover:bg-white/10'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
            onClick={onClose}
            aria-label="Cerrar menú"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {user ? (
            <>
              {appLinksFor(user).map((item) => (
                <MenuItem
                  key={item.to}
                  {...item}
                  active={isActivePath(pathname, item.to)}
                  onClick={onClose}
                  modoOscuro={modoOscuro}
                />
              ))}
              {isSuperAdmin && !subscriptionBlocked && (
                <MenuItem
                  to="/admin/dashboard"
                  icon={FiUser}
                  label="Administración"
                  active={isActivePath(pathname, '/admin')}
                  onClick={onClose}
                  modoOscuro={modoOscuro}
                />
              )}
              {!subscriptionBlocked && (
                <MenuItem
                  to="/cuenta"
                  icon={FiSettings}
                  label="Mi cuenta"
                  active={isActivePath(pathname, '/cuenta')}
                  onClick={onClose}
                  modoOscuro={modoOscuro}
                />
              )}
            </>
          ) : (
            <>
              {guestLinks.map((item) => (
                <MenuItem
                  key={item.to}
                  {...item}
                  active={isActivePath(pathname, item.to)}
                  onClick={onClose}
                  modoOscuro={modoOscuro}
                />
              ))}
              <MenuItem
                icon={FiMail}
                label="Contacto"
                active={false}
                onClick={() => {
                  onClose();
                  window.dispatchEvent(new CustomEvent('openContactModal'));
                }}
                modoOscuro={modoOscuro}
              />
            </>
          )}
        </nav>

        <div className={`px-5 pt-4 border-t ${divide} space-y-2.5`}>
          {user ? (
            <button
              type="button"
              onClick={handleLogout}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border transition-colors ${
                modoOscuro
                  ? 'border-red-500/40 text-red-400 hover:bg-red-500/15'
                  : 'border-red-200 text-red-600 hover:bg-red-50'
              }`}
            >
              <FiLogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          ) : (
            <>
              <Link
                to="/register"
                onClick={onClose}
                className="w-full px-4 py-3 rounded-xl text-sm font-semibold flex items-center justify-center bg-[#1F80FF] text-white hover:bg-[#004AAD] transition-colors"
              >
                Empezar gratis
              </Link>
              <Link
                to="/login"
                onClick={onClose}
                className={`w-full px-4 py-3 rounded-xl text-sm font-semibold flex items-center justify-center border transition-colors ${
                  modoOscuro
                    ? 'border-gray-700 text-gray-200 hover:border-[#1F80FF] hover:text-[#5CA3FF]'
                    : 'border-gray-200 text-gray-800 hover:border-[#004AAD] hover:text-[#004AAD]'
                }`}
              >
                Iniciar sesión
              </Link>
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default MenuMobile;
